import api from '../../../../api';

export interface ResultsScore {
  maximum: number;
  achievable: number;
  achieved: number;
}

interface ScoreBase {
  exerciseId: string;
  exerciseType: 'medialink' | 'user-material';
  duration?: number;
  achievedAt?: string;
  amountOfItems: number;
  isAssignment: boolean;
  isZeroScore: boolean;
  score: ResultsScore;
  session?: string;
  needsGrading?: boolean;
  isLti?: boolean;
  isSmartschoolSynced?: boolean;
  completedBySystem: boolean;
  amountOfSessions: number;
}

interface AssignmentScore extends ScoreBase {
  isAssignment: true;
  assignmentStartDate: string;
  assignmentEndDate: string;
}

interface FreeExerciseScore extends ScoreBase {
  isAssignment: false;
}

export type Score = AssignmentScore | FreeExerciseScore;

export type NodeInfo = {
  id: string;
  title: string;
  prefix?: string;
  parentIds: string[];
};

export type Medialink = {
  id: string;
  exerciseId?: string;
  name: string;
  kind: string;
  exerciseCategories?: { name: string }[];
  showScore: boolean;
  contentType: {
    name: string;
    icon?: string;
    iconClassName?: string;
    faqTutorial?: string;
  };
};

export type UserMaterial = {
  id: string;
  name: string;
  contentType?: undefined;
  userMaxScore?: number;
};

export type Member = {
  id: string;
  firstName: string;
  lastName: string;
};

export type LessonResults = {
  /**
   * The scores per node id per member id.
   */
  scores: Record<string, Record<string, Score[]>>;
  /**
   * key/value pairs of node id and node.
   */
  nodes: Record<string, NodeInfo>;
  /**
   * key/value pairs of medialink id and medialink.
   */
  mediaLinks: Record<string, Medialink>;
  /**
   * key/value pairs of user-material id and user-material.
   */
  userMaterials: Record<string, UserMaterial>;
  /**
   * key/value pairs of member id and member.
   */
  members: Record<string, Member>;
};

export async function getLessonResults(moduleId: string, groupId: string, search: URLSearchParams) {
  const { data } = await api.get<LessonResults>(
    `/studio/modules/${moduleId}/exercise-scores/groups/${groupId}/lessons?${search}`,
  );

  return data;
}

export type Theme = {
  id: string;
  name: string;
};

export type Category = {
  id: string;
  name: string;
  theme?: string;
};

export type ThemeResults = {
  scores: Record<string, Record<string, Score[]>>;
  themes: Record<string, Theme>;
  categories: Record<string, Category>;
  mediaLinks: Record<string, Medialink>;
  members: Record<string, Member>;
};

export async function getThemeResults(moduleId: string, groupId: string, search: URLSearchParams) {
  const { data } = await api.get<ThemeResults>(
    `/studio/modules/${moduleId}/exercise-scores/groups/${groupId}/themes?${search}`,
  );

  return data;
}
